import { useEffect, useState, FC, useMemo } from "react";

import "./dev.scss";
import {
  PermissionUser,
  listPermissionUser,
  postPermissionAddUser,
  postPermissionRemoveUser,
} from "@rubi/api-generated";
import { useLocation, useParams } from "react-router";
import { Link } from "react-router-dom";

import { DevPage, Section, ErrorMsg, Breadcrumbs, Crumb } from "./common";

const PermissionUserRow: FC<{
  permissionUser: PermissionUser;
  permissionId: number;
}> = ({
  permissionUser: { user_id, first_name, last_name, email },
  permissionId: permission_id,
}) => {
  const [revokeButtonActive, setRevokeButtonActive] = useState<boolean>(true);
  const [revokeErrorMsg, setRevokeErrorMsg] = useState<string>("");

  const revokePermission = async (permissionId: number, userId: string) => {
    const response = await postPermissionRemoveUser({
      path: { permission_id: permissionId },
      body: { user_id: userId },
      throwOnError: false,
    });
    if (response.status === 200) {
      console.log("Revoked " + permissionId + " from " + userId);
    } else {
      setRevokeErrorMsg(
        "" + response.status + " " + response.error?.console_msg,
      );
      console.log(response);
    }
  };

  return (
    <tr>
      <td className="mono">{user_id}</td>
      <td>{first_name}</td>
      <td>{last_name}</td>
      <td>{email}</td>
      <td style={{ textAlign: "right" }}>
        <button
          onClick={() => {
            revokePermission(permission_id, user_id);
            setRevokeButtonActive(false);
          }}
          disabled={!revokeButtonActive}
        >
          Revoke
        </button>
        <ErrorMsg msg={revokeErrorMsg} />
      </td>
    </tr>
  );
};

const PermissionUserTable: FC<{
  permissionUsers: PermissionUser[];
  permissionId: number;
}> = ({ permissionUsers, permissionId }) => {
  return (
    <table className="dev-table">
      <thead>
        <tr>
          <th>user_id</th>
          <th>first_name</th>
          <th>last_name</th>
          <th>email</th>
          <th>Revoke</th>
        </tr>
      </thead>
      <tbody>
        {permissionUsers.map((permissionUser) => (
          <PermissionUserRow
            key={permissionUser.user_id}
            permissionUser={permissionUser}
            permissionId={permissionId}
          />
        ))}
      </tbody>
    </table>
  );
};

export const DevPermissionPage: FC = () => {
  const [usersList, setUsersList] = useState<PermissionUser[]>([]);
  const [loadPermissionUsersErrorMsg, setLoadPermissionUsersErrorMsg] =
    useState<string | null>(null);
  const location = useLocation();
  const { id } = useParams();
  const permissionId = useMemo(() => parseInt(id || "0"), [id]);
  const [addPermissionUserId, setAddPermissionUserId] = useState<string>(
    "00000000-0000-0000-0000-000000000000",
  );
  const [grantErrorMsg, setGrantErrorMsg] = useState<string | null>(null);

  const loadPermissionUsers = async () => {
    const response = await listPermissionUser({
      path: { permission_id: permissionId },
      throwOnError: false,
    });
    if (response.status === 200) {
      const data = response.data!;
      setUsersList(data);
      setLoadPermissionUsersErrorMsg(null);
    } else {
      setUsersList([]);
      setLoadPermissionUsersErrorMsg(
        "" + response.status + " " + response.error?.console_msg,
      );
      console.log(response);
    }
  };

  const grantPermission = async (permissionId: number, userId: string) => {
    const response = await postPermissionAddUser({
      path: { permission_id: permissionId },
      body: { user_id: userId },
      throwOnError: false,
    });
    if (response.status === 200) {
      console.log("Granted " + permissionId + " to " + userId);
    } else {
      setGrantErrorMsg(
        "" + response.status + " " + response.error?.console_msg,
      );
      console.log(response);
    }
  };

  useEffect(() => {
    loadPermissionUsers();
  }, []);

  return (
    <DevPage
      title={location.state.permission.title + " Permission"}
      breadcrumbs={
        <Breadcrumbs>
          <Crumb>
            <Link to="/">Home</Link>
          </Crumb>
          <Crumb>
            <Link to="/dev_admin">Developer and Administrator Utilities</Link>
          </Crumb>
          <Crumb>
            <Link to="/dev_admin/permission">Permissions</Link>
          </Crumb>
          <Crumb>{location.state.permission.title}</Crumb>
        </Breadcrumbs>
      }
    >
      <Section subtitle="Grant permission to user">
        <input
          value={addPermissionUserId}
          onChange={(e) => setAddPermissionUserId(e.target.value)}
        />
        <button
          onClick={() => grantPermission(permissionId, addPermissionUserId)}
        >
          Grant
        </button>
        <ErrorMsg msg={grantErrorMsg} />
      </Section>
      <Section subtitle="Users">
        <button onClick={loadPermissionUsers}>Reload</button>
        <ErrorMsg msg={loadPermissionUsersErrorMsg} />
        <PermissionUserTable
          permissionUsers={usersList}
          permissionId={permissionId}
        />
      </Section>
    </DevPage>
  );
};
